import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

interface DisclosureSectionProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
}

export function DisclosureSection({
  title,
  description,
  children,
  defaultOpen = false,
  actions,
  className,
  contentClassName,
}: DisclosureSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    <section className={cn('rounded-2xl border border-shell-border bg-shell-card shadow-sm', className)}>
      <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
          aria-expanded={isOpen}
          aria-controls={contentId}
          onClick={() => setIsOpen((value) => !value)}
        >
          <span className="mt-0.5 rounded-md p-1 text-neutral-500">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
          <span className="min-w-0">
            <span className="block text-base font-semibold text-neutral-900">{title}</span>
            {description && <span className="mt-1 block text-sm text-neutral-600">{description}</span>}
          </span>
        </button>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>

      {isOpen && (
        <div id={contentId} className={cn('border-t border-shell-border p-5', contentClassName)}>
          {children}
        </div>
      )}
    </section>
  );
}
